/**
 * Cost Template Utilities
 * Pre-populated cost structures for project setup
 */

import { CostTemplate, CostItem, Project } from '../types';

/**
 * Default templates for common project types
 */
export const DEFAULT_COST_TEMPLATES: Record<string, Omit<CostTemplate, 'id' | 'organizationId' | 'createdAt' | 'createdBy'>> = {
  'construction-standard': {
    name: 'Standard Construction Project',
    description: 'Default template for construction projects with materials and labor',
    projectType: 'Construction',
    isDefault: true,
    costItems: [
      {
        description: 'Land Acquisition',
        category: 'Land',
        defaultAmount: 0,
        code: 'LAND-001'
      },
      {
        description: 'Design and Permits',
        category: 'Design',
        defaultAmount: 0,
        code: 'DSGN-001'
      },
      {
        description: 'Site Preparation',
        category: 'Site Preparation',
        defaultAmount: 0,
        code: 'SITE-001'
      },
      {
        description: 'Foundation Work',
        category: 'Foundation',
        defaultAmount: 0,
        code: 'FNDN-001'
      },
      {
        description: 'Framing and Structure',
        category: 'Framing',
        defaultAmount: 0,
        code: 'FRAM-001'
      },
      {
        description: 'Exterior Shell and Roofing',
        category: 'Exterior',
        defaultAmount: 0,
        code: 'EXT-001'
      },
      {
        description: 'Rough-ins (Plumbing, Electrical, HVAC)',
        category: 'Rough-ins',
        defaultAmount: 0,
        code: 'ROUGH-001'
      },
      {
        description: 'Insulation and Drywall',
        category: 'Interior',
        defaultAmount: 0,
        code: 'INSUL-001'
      },
      {
        description: 'Interior Finishes',
        category: 'Interior',
        defaultAmount: 0,
        code: 'INTFIN-001'
      },
      {
        description: 'Finishing Touches and Landscaping',
        category: 'Finishing',
        defaultAmount: 0,
        code: 'FINISH-001'
      },
      {
        description: 'Sales and Marketing',
        category: 'Marketing',
        defaultAmount: 0,
        code: 'MKT-001'
      },
      {
        description: 'Miscellaneous and Contingency',
        category: 'Miscellaneous',
        defaultAmount: 0,
        code: 'MISC-001'
      }
    ]
  },
  'software-standard': {
    name: 'Standard Software Project',
    description: 'Default template for software projects with engineering and infrastructure',
    projectType: 'Software',
    isDefault: true,
    costItems: [
      {
        description: 'Requirements Analysis',
        category: 'Planning',
        defaultAmount: 0,
        code: 'REQ-001'
      },
      {
        description: 'System Design',
        category: 'Design',
        defaultAmount: 0,
        code: 'DESIGN-001'
      },
      {
        description: 'Senior Engineer ($/hr)',
        category: 'Engineering',
        defaultAmount: 150,
        code: 'ENG-SR-001'
      },
      {
        description: 'Mid-Level Engineer ($/hr)',
        category: 'Engineering',
        defaultAmount: 100,
        code: 'ENG-MD-001'
      },
      {
        description: 'Junior Engineer ($/hr)',
        category: 'Engineering',
        defaultAmount: 60,
        code: 'ENG-JR-001'
      },
      {
        description: 'QA Engineer ($/hr)',
        category: 'QA',
        defaultAmount: 80,
        code: 'QA-001'
      },
      {
        description: 'Cloud Infrastructure ($/month)',
        category: 'Infrastructure',
        defaultAmount: 2000,
        code: 'INF-CLD-001'
      },
      {
        description: 'SaaS Subscriptions ($/month)',
        category: 'Infrastructure',
        defaultAmount: 0,
        code: 'INF-SAAS-001'
      },
      {
        description: 'Testing and Deployment',
        category: 'Operations',
        defaultAmount: 0,
        code: 'OPS-TEST-001'
      },
      {
        description: 'Maintenance and Support',
        category: 'Operations',
        defaultAmount: 0,
        code: 'OPS-MAINT-001'
      }
    ]
  },
  'finance-standard': {
    name: 'Standard Finance Project',
    description: 'Default template for finance projects',
    projectType: 'Finance',
    isDefault: true,
    costItems: [
      {
        description: 'Analyst ($/hour)',
        category: 'Personnel',
        defaultAmount: 150,
        code: 'PSN-ANAL-001'
      },
      {
        description: 'Manager ($/hour)',
        category: 'Personnel',
        defaultAmount: 200,
        code: 'PSN-MGR-001'
      },
      {
        description: 'System Access & Licenses',
        category: 'Technology',
        defaultAmount: 500,
        code: 'TECH-LIC-001'
      },
      {
        description: 'Third-party Data Services',
        category: 'Technology',
        defaultAmount: 1000,
        code: 'TECH-DATA-001'
      }
    ]
  }
};

/**
 * Create a cost template for a project
 */
export function createCostTemplate(config: {
  name: string;
  projectType: 'Software' | 'Construction' | 'Finance' | 'Other';
  organizationId: string;
  createdBy: string;
  costItems: Array<{
    description: string;
    category: string;
    defaultAmount?: number;
    code?: string;
  }>;
}): CostTemplate {
  const id = Math.random().toString(36).substr(2, 9);
  return {
    id,
    organizationId: config.organizationId,
    name: config.name,
    projectType: config.projectType,
    costItems: config.costItems,
    isDefault: false,
    createdAt: new Date().toISOString(),
    createdBy: config.createdBy
  };
}

/**
 * Get default template for project type
 */
export function getDefaultTemplate(projectType: 'Software' | 'Construction' | 'Finance' | 'Other'): Omit<CostTemplate, 'id' | 'organizationId' | 'createdAt' | 'createdBy'> | null {
  if (projectType === 'Construction') {
    return DEFAULT_COST_TEMPLATES['construction-standard'];
  }
  if (projectType === 'Software') {
    return DEFAULT_COST_TEMPLATES['software-standard'];
  }
  if (projectType === 'Finance') {
    return DEFAULT_COST_TEMPLATES['finance-standard'];
  }
  return null;
}

/**
 * Apply template to project (creates cost items)
 */
export function applyTemplateToProject(
  project: Project,
  template: CostTemplate
): CostItem[] {
  return template.costItems.map((item, index) => ({
    id: `cost-${Date.now()}-${index}`,
    code: item.code,
    description: item.description,
    amount: item.defaultAmount || 0,
    category: item.category
  }));
}

/**
 * Generate cost item code if not provided
 */
export function generateCostCode(category: string, index: number): string {
  const categoryShort = category.substring(0, 3).toUpperCase();
  return `${categoryShort}-${String(index + 1).padStart(3, '0')}`;
}

/**
 * Validate cost template structure
 */
export function isValidTemplate(template: CostTemplate): boolean {
  if (!template.name || !template.projectType) return false;
  if (!Array.isArray(template.costItems) || template.costItems.length === 0) return false;

  return template.costItems.every(item =>
    item.description && item.category
  );
}

/**
 * Merge custom items with template items
 */
export function mergeTemplateWithCustomItems(
  template: CostTemplate,
  customItems: CostItem[]
): CostItem[] {
  const templateItems = template.costItems.map((item, index) => ({
    id: `template-${index}`,
    code: item.code || generateCostCode(item.category, index),
    description: item.description,
    amount: item.defaultAmount || 0,
    category: item.category
  }));

  return [...templateItems, ...customItems.filter(item => 
    !templateItems.find(tItem => tItem.description === item.description)
  )];
}

/**
 * Get all default cost templates as full CostTemplate objects
 */
export function getDefaultCostTemplates(): CostTemplate[] {
  return Object.entries(DEFAULT_COST_TEMPLATES).map(([key, template]) => ({
    id: key,
    organizationId: 'default',
    name: template.name,
    description: template.description,
    projectType: template.projectType,
    costItems: template.costItems,
    isDefault: template.isDefault,
    createdAt: new Date().toISOString(),
    createdBy: 'system'
  }));
}

/**
 * Apply a cost template to a project and return the updated project
 */
export function applyCostTemplate(project: Project, template: CostTemplate): Project {
  const costItems = applyTemplateToProject(project, template);
  return {
    ...project,
    costs: costItems,
    updatedAt: new Date().toISOString()
  };
}
